import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, CheckCircle2, Copy, Info, PartyPopper } from 'lucide-react';
import { useStore, fmtIDR, fmtDate, todayISO } from '../lib/store';
import { GUARD_CHECKLIST, GUARD_CRITICAL_GROUPS, ALL_GUARD_ITEMS, GUARD_ASK_MESSAGES } from '../lib/checklists';
import { BackBar, Money, EmptyState, Field, inputCls, EASE } from '../components/Bits';

export default function PaymentGuard() {
  const { id } = useParams();
  const { state, setGuardCheck, recordPayment, setDecisionNote } = useStore();
  const navigate = useNavigate();
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decision, setDecision] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [rec, setRec] = useState(null);
  const [copied, setCopied] = useState(null);

  const copyAsk = async (key) => {
    const msg = GUARD_ASK_MESSAGES[key];
    if (!msg) return;
    try {
      await navigator.clipboard.writeText(msg);
    } catch { /* clipboard unavailable */ }
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 2500);
  };

  const payment = state.payments.find((p) => p.id === id);
  if (!payment) return <EmptyState title="Pembayaran tidak ditemukan" />;
  const vendor = state.vendors.find((v) => v.id === payment.vendorId);
  const checks = state.guardChecks[id] || {};

  const critical = GUARD_CHECKLIST.filter((g) => GUARD_CRITICAL_GROUPS.includes(g.id)).flatMap((g) => g.items);
  const unresolved = critical.filter((i) => !checks[i.key]);
  const allClear = ALL_GUARD_ITEMS.every((i) => checks[i.key]);

  const submitRecord = (e) => {
    e.preventDefault();
    recordPayment(id, { amount: Number(rec.amount) || payment.amount, date: rec.date || todayISO(), stage: rec.stage, reference: rec.reference, note: rec.note });
    setRecordOpen(false);
    setDone(true);
  };

  if (done) {
    return (
      <div className="pt-16 text-center">
        <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, ease: EASE }}>
          <PartyPopper size={42} className="mx-auto text-terra" strokeWidth={1.5} />
          <h1 data-testid="payment-recorded-title" className="mt-5 font-serif text-2xl font-medium text-ink">Pembayaran sudah dicatat.</h1>
          <p className="mt-2 text-sm text-cashmere">Catatan tersimpan rapi. Sebelum sah, jangan asal pasrah. 😄</p>
          <Link data-testid="payment-recorded-back" to="/app/pembayaran" className="mt-7 inline-flex rounded-full bg-ink text-ivory px-6 py-3 text-sm font-bold">
            Kembali ke Pembayaran
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="payment-guard-page" className="space-y-6">
      <BackBar to="/app/pembayaran" title="Pembayaran" />

      {/* Pre-flight header */}
      <div className="relative overflow-hidden rounded-[20px] bg-white border border-line soft-shadow p-6">
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none indigo-haze" />
        <div className="relative">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra">Sebelum Transfer</p>
          <p className="mt-3 text-sm font-semibold text-cashmere">{vendor?.name}</p>
          <p className="text-xs text-cashmere">{payment.label}</p>
          <Money data-testid="guard-amount" className="block mt-3 text-[2.6rem] leading-none font-semibold tracking-tight text-ink">{fmtIDR(payment.amount)}</Money>
          <p className="font-mono text-[11px] tnum text-cashmere mt-2">Jatuh tempo {fmtDate(payment.dueDate)}</p>
          <p className="mt-4 font-serif italic text-[15px] text-ink/75 border-t border-line pt-3">Cek dulu, sebelum bayar.</p>
          <p className="mt-1.5 text-xs text-cashmere leading-relaxed">Luangkan sebentar untuk memastikan hal penting sudah kamu tinjau sebelum pembayaran ini.</p>
          <div className="mt-3 flex gap-2">
            <span data-testid="guard-summary-checked" className="rounded-full bg-[#EBF2EC] border border-[#CBDCCE] text-sage text-[11px] font-semibold px-3 py-1 tnum">{critical.filter((i) => checks[i.key]).length} sudah dicek</span>
            <span data-testid="guard-summary-pending" className="rounded-full bg-[#FBF1DF] border border-[#EEDCB9] text-amberw text-[11px] font-semibold px-3 py-1 tnum">{unresolved.length} perlu dicek</span>
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="space-y-4">
        {GUARD_CHECKLIST.map((g) => (
          <div key={g.id} className="rounded-2xl border border-ink/10 bg-white p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra px-1 mb-2.5">{g.title}</p>
            <div className="space-y-1.5">
              {g.items.map((item) => {
                const on = !!checks[item.key];
                return (
                  <button
                    key={item.key}
                    data-testid={`guard-check-${item.key}`}
                    onClick={() => setGuardCheck(id, item.key, !on)}
                    className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors ${
                      on ? 'border-[#CBDCCE] bg-[#EBF2EC]/60' : 'border-line bg-white hover:border-ink/20'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${on ? 'bg-sage border-sage' : 'border-ink/25 bg-white'}`}>
                      {on && <Check size={12} strokeWidth={3.5} className="text-white" />}
                    </span>
                    <span className={`text-xs font-medium leading-snug ${on ? 'text-ink' : 'text-slate-600'}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Result */}
      <AnimatePresence mode="wait">
        {allClear ? (
          <motion.div key="clear" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="rounded-[20px] border border-[#CBDCCE] bg-[#EBF2EC]/60 p-5">
            <div className="flex items-center gap-2.5 mb-2">
              <CheckCircle2 size={18} className="text-sage" />
              <p data-testid="guard-state-clear" className="font-serif text-lg font-medium text-ink">Checklist utama sudah ditinjau.</p>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">Keputusan pembayaran tetap di tangan kamu dan pasangan.</p>
            <button
              data-testid="guard-record-payment-btn"
              onClick={() => { setRec({ amount: payment.amount, date: todayISO(), stage: payment.label, reference: '', note: '' }); setRecordOpen(true); }}
              className="mt-4 w-full rounded-full bg-ink text-ivory py-3.5 text-sm font-bold hover:bg-inkdeep transition-colors"
            >
              Catat Pembayaran
            </button>
          </motion.div>
        ) : (
          <motion.div key="warn" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="rounded-[20px] border border-[#EEDCB9] bg-[#FBF1DF]/70 p-5">
            <p data-testid="guard-state-pending" className="font-serif text-lg font-medium text-ink">Perlu Cek Dulu</p>
            <p className="mt-1.5 text-xs text-cashmere leading-relaxed">Masih ada beberapa hal penting yang belum kamu tandai sebagai sudah dicek.</p>
            <p className="mt-4 font-mono text-[10px] tnum uppercase tracking-wider text-amberw">Sebelum {payment.label} · {fmtIDR(payment.amount)}</p>
            <ul className="mt-2 space-y-2">
              {unresolved.slice(0, 3).map((u) => (
                <li key={u.key} className="rounded-xl bg-white/80 border border-[#EEDCB9]/70 px-3.5 py-2.5">
                  <p className="text-xs text-pewter font-medium leading-snug">{u.label}</p>
                  {GUARD_ASK_MESSAGES[u.key] && (
                    <button
                      data-testid={`guard-ask-${u.key}`}
                      onClick={() => copyAsk(u.key)}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-terra hover:underline"
                    >
                      {copied === u.key ? (<><Check size={11} strokeWidth={3} /> Tersalin — tinggal tempel ke chat vendor</>) : (<><Copy size={11} /> Salin Pesan Tanya ke Vendor</>)}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2">
              <button data-testid="guard-complete-btn" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="w-full rounded-full bg-ink text-ivory py-3.5 text-sm font-bold hover:bg-inkdeep transition-colors">
                Lengkapi Pengecekan
              </button>
              <button data-testid="guard-decision-btn" onClick={() => setDecisionOpen((o) => !o)} className="w-full rounded-full border border-ink/20 bg-white py-3.5 text-sm font-semibold text-ink hover:bg-bone transition-colors">
                Catat Keputusan
              </button>
            </div>
            <AnimatePresence>
              {decisionOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                  <div className="mt-3 space-y-2">
                    <textarea data-testid="guard-decision-input" rows={2} className={inputCls + ' text-xs'} placeholder="Contoh: Tetap transfer setelah konfirmasi lisan dengan PIC." value={decision} onChange={(e) => setDecision(e.target.value)} />
                    <button
                      data-testid="guard-decision-save"
                      onClick={() => { if (decision.trim()) { setDecisionNote(id, decision.trim()); setDecisionOpen(false); setDecision(''); } }}
                      className="rounded-full bg-terra text-ivory px-5 py-2.5 text-xs font-bold"
                    >
                      Simpan Keputusan
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {payment.decisionNote && (
              <p className="mt-3 text-[11px] text-slate-600 italic border-l-2 border-gold/60 pl-3">Keputusan dicatat {fmtDate(payment.decisionNote.at)}: “{payment.decisionNote.text}”</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Record form */}
      <AnimatePresence>
        {recordOpen && rec && (
          <motion.form
            data-testid="record-payment-form"
            onSubmit={submitRecord}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="rounded-2xl border border-ink/10 bg-white p-5 space-y-4">
              <p className="font-serif text-lg font-semibold text-ink">Catat Pembayaran</p>
              <Field label="Jumlah (Rp)">
                <input data-testid="record-amount-input" type="number" min="0" className={inputCls} value={rec.amount} onChange={(e) => setRec({ ...rec, amount: e.target.value })} required />
              </Field>
              <Field label="Tanggal transfer">
                <input data-testid="record-date-input" type="date" className={inputCls} value={rec.date} onChange={(e) => setRec({ ...rec, date: e.target.value })} required />
              </Field>
              <Field label="Tahap pembayaran">
                <input data-testid="record-stage-input" className={inputCls} value={rec.stage} onChange={(e) => setRec({ ...rec, stage: e.target.value })} />
              </Field>
              <Field label="Catatan referensi" optional>
                <input data-testid="record-reference-input" className={inputCls} placeholder="TRF-WO-002" value={rec.reference} onChange={(e) => setRec({ ...rec, reference: e.target.value })} />
              </Field>
              <Field label="Catatan" optional>
                <input data-testid="record-note-input" className={inputCls} placeholder="Bukti transfer tersimpan di WhatsApp." value={rec.note} onChange={(e) => setRec({ ...rec, note: e.target.value })} />
              </Field>
              <button data-testid="record-payment-submit" type="submit" className="w-full rounded-full bg-terra text-ivory py-3.5 text-sm font-bold hover:brightness-110 transition">
                Simpan Pembayaran
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <Link data-testid="guard-disclaimer-link" to="/app/tentang" className="flex items-center gap-2 text-[11px] text-slate-400 hover:text-slate-600">
        <Info size={12} /> SebelumSah tidak mengeksekusi transaksi & tidak menjamin keamanan pembayaran
      </Link>
    </div>
  );
}
